<script>
	import { onMount } from 'svelte';
	import Loader from './Loader.svelte';
	import TabView from './TabView.svelte';
	import TabPane from './TabPane.svelte';
	import GameCard from './GameCard.svelte';
	import WidgetSelect from './WidgetSelect.svelte';
	import {
		buildQuery,
		coachUrl,
		gameUrl,
		getApiUrl,
		injectGlobalStyles,
		loadJson,
		normalizeCollection,
		normalizeText,
		playerUrl
	} from './shared.js';

	export let teamId = '';
	export let tag = 'mlbl';
	export let season = '';
	export let lang = 'ru';

	let host;
	let loading = false;
	let rosterLoading = false;
	let gamesLoading = false;
	let errorMessage = '';
	let rosterErrorMessage = '';
	let gamesErrorMessage = '';
	let team = null;
	let roster = null;
	let games = [];
	let selectedSeason = '';
	let selectedGamesSeason = '';
	let selectedCompId = '';
	let selectedViewMode = 'cards';
	let playersSortField = '';
	let playersSortDirection = 'asc';
	let lastRosterKey = '';

	const viewModeOptions = [
		{ value: 'cards', label: 'Карточки' },
		{ value: 'table', label: 'Таблица' }
	];

	$: seasonOptions = normalizeCollection(team?.seasons).map((item, index) => ({
		value: String(item?.id || item?.name || `season-${index}`),
		label: normalizeText(item?.name || item?.id, `Сезон ${index + 1}`)
	}));

	$: seasonComps = normalizeCollection(team?.comps).filter((item) => String(item?.season ?? '') === String(selectedSeason ?? ''));

	$: compOptions = seasonComps.map((item, index) => ({
		value: String(item?.id || `comp-${index}`),
		label: normalizeText(item?.name || item?.abcName, `Турнир ${index + 1}`)
	}));

	$: selectedComp = seasonComps.find((item) => String(item?.id) === String(selectedCompId));

	$: players = normalizeCollection(roster?.players);
	$: coaches = normalizeCollection(roster?.coaches);
	$: sortedPlayers = [...players].sort((left, right) => {
		if (!playersSortField) {
			return 0;
		}

		const leftValue = Number(left?.[playersSortField]);
		const rightValue = Number(right?.[playersSortField]);
		const leftNumber = Number.isFinite(leftValue) ? leftValue : -1;
		const rightNumber = Number.isFinite(rightValue) ? rightValue : -1;
		const compare = leftNumber - rightNumber;
		return playersSortDirection === 'asc' ? compare : -compare;
	});

	$: averageHeight = getAverage(players, 'height');
	$: averageWeight = getAverage(players, 'weight');
	$: averageAge = getAverage(players, 'age');

	$: if (selectedSeason && compOptions.length && !compOptions.some((item) => item.value === selectedCompId)) {
		selectedCompId = compOptions[0].value;
	}

	$: if (team && selectedSeason && selectedCompId) {
		const nextKey = `${selectedSeason}|${selectedCompId}`;
		if (nextKey !== lastRosterKey) {
		lastRosterKey = nextKey;
		loadRoster();
		}
	}

	function getTeamTitle(data) {
		return data?.name || data?.shortName || data?.label  || 'Команда';
	}

	function getTeamSubtitle(data) {
		return data?.regionName || data?.region?.name || '—';
	}

	function getInitials(data) {
		return (data?.label?.[0] || data?.shortName?.[0] || data?.name?.[0] || 'T').toUpperCase();
	}

	function getPersonTitle(person) {
		return [person?.lastName, person?.firstName, person?.secondName].filter(Boolean).join(' ') || '—';
	}

	function getPersonInitials(person, fallback = 'P') {
		const first = person?.firstName?.[0] || '';
		const last = person?.lastName?.[0] || '';
		return `${first}${last}`.trim() || fallback;
	}

	function getAverage(items, key) {
		const values = (items || [])
		.map((item) => Number(item?.[key]))
		.filter((value) => Number.isFinite(value) && value > 0);
			if (!values.length) {
				return '—';
			}
			const avg = values.reduce((sum, current) => sum + current, 0) / values.length;
			return avg.toFixed(1);
	}

	function togglePlayersSort(field) {
		if (playersSortField === field) {
			playersSortDirection = playersSortDirection === 'asc' ? 'desc' : 'asc';
			return;
		}
		playersSortField = field;
		playersSortDirection = 'asc';
	}

	function getSortLabel(field, label) {
		if (playersSortField !== field) {
			return label;
		}
		return `${label} ${playersSortDirection === 'asc' ? '↑' : '↓'}`;
	}

	function getActiveTag() {
		return selectedComp?.tag || team?.selectedTag || tag;
	}

	function openPlayer(person) {
		if (!person?.personId) {
			return;
		}
		window.location.href = playerUrl(person.personId, {
			tag: getActiveTag(),
			teamId,
			compId: selectedCompId
		});
	}

	function openCoach(person) {
		if (!person?.personId) {
			return;
		}
		window.location.href = coachUrl(person.personId, {
			tag: getActiveTag(),
			teamId,
			compId: selectedCompId
		});
	}

	function openGame(game) {
		if (!game?.game?.id) {
			return;
		}
		window.location.href = gameUrl(game.game.id, {
			tag: getActiveTag(),
			teamId,
			compId: selectedCompId
		});
	}

	function initializeRosterFilters() {
		const seasons = normalizeCollection(team?.seasons);
		selectedSeason = seasons.length > 0
			? String(seasons[0]?.id || seasons[0]?.name || 'season-0')
			: '';

		const currentTag = team?.selectedTag || tag;
		const compsBySeason = normalizeCollection(team?.comps).filter((item) => String(item?.season ?? '') === String(selectedSeason ?? ''));
		const preferredComp = compsBySeason.find((item) => item?.tag === currentTag && item?.id);
		selectedCompId = String(preferredComp?.id || compsBySeason[0]?.id || '');

		// Инициализируем сезон для матчей
		selectedGamesSeason = selectedSeason;
	}

	async function loadTeam() {
		if (!teamId) {
			errorMessage = 'Не указан идентификатор команды';
			team = null;
			roster = null;
			return;
		}

		loading = true;
		errorMessage = '';
		try {
			const url = `${getApiUrl()}/ib/team/${encodeURIComponent(teamId)}/info${buildQuery({ tag, season, lang })}`;
			team = await loadJson(url);
			initializeRosterFilters();
		} catch (error) {
			console.error('Load team info error:', error);
			errorMessage = 'Ошибка загрузки команды';
			team = null;
			roster = null;
		} finally {
			loading = false;
		}
	}

	async function loadRoster() {
		if (!teamId || !selectedSeason) {
			roster = null;
			return;
		}

		rosterLoading = true;
		rosterErrorMessage = '';
		try {
			const currentTag = getActiveTag();
			const url = `${getApiUrl()}/ib/team/${encodeURIComponent(teamId)}/roster${buildQuery({ tag: currentTag, season: selectedSeason, lang })}`;
			roster = await loadJson(url);
		} catch (error) {
			console.error('Load team roster error:', error);
			rosterErrorMessage = 'Ошибка загрузки состава';
			roster = null;
		} finally {
			rosterLoading = false;
		}
	}

	async function loadGames() {
		if (!teamId || !selectedGamesSeason) {
			games = [];
			return;
		}

		gamesLoading = true;
		gamesErrorMessage = '';
		try {
			const url = `${getApiUrl()}/ib/team/${encodeURIComponent(teamId)}/games${buildQuery({ tag, season: selectedGamesSeason, lang })}`;
			const gamesData = await loadJson(url);
			games = Array.isArray(gamesData) ? gamesData : [];
		} catch (error) {
			console.error('Load team games error:', error);
			gamesErrorMessage = 'Ошибка загрузки матчей';
			games = [];
		} finally {
			gamesLoading = false;
		}
	}

	$: if (team && selectedGamesSeason) {
		loadGames();
	}

	onMount(() => {
		const root = host?.getRootNode();
		if (root instanceof ShadowRoot) {
			injectGlobalStyles(root);
		}

		loadTeam();
	});
</script>

<div class="widget-card" bind:this={host}>
	{#if loading}
		<div class="widget-card__status"><Loader size="64px" /></div>
	{:else if errorMessage}
		<div class="widget-card__status">{errorMessage}</div>
	{:else if team}
		<div class="profile">
			<div class="profile__head">
				<div class="profile__media">
					{#if team.logo}
						<img class="profile__image" src={team.logo} alt={getTeamTitle(team)}>
					{:else}
						<div class="profile__placeholder">{getInitials(team)}</div>
					{/if}
				</div>

				<div class="profile__title">
					<h3>{getTeamTitle(team)}</h3>
					<p>{getTeamSubtitle(team)}</p>
				</div>
			</div>

			<TabView>
				
				<TabPane title="Состав команды">
					<div class="widget-tabs__body">
						<div class="roster-toolbar">
							<div class="roster-toolbar__item">
								<WidgetSelect
									className="--black"
									name="team-roster-season"
									options={seasonOptions}
									bind:value={selectedSeason}
									disabled={loading || !seasonOptions.length}
								/>
							</div>

							<div class="roster-toolbar__item">
								<WidgetSelect
									className="--black"
									name="team-roster-comp"
									options={compOptions}
									bind:value={selectedCompId}
									disabled={loading || !compOptions.length}
								/>
							</div>

							<div class="roster-toolbar__spacer"></div>

							<div class="roster-toolbar__item roster-toolbar__item--view">
								<WidgetSelect
									className="--black"
									name="team-roster-view"
									options={viewModeOptions}
									bind:value={selectedViewMode}
									disabled={rosterLoading}
								/>
							</div>
						</div>

						{#if rosterLoading}
							<div class="widget-card__status"><Loader size="64px" /></div>
						{:else if rosterErrorMessage}
							<div class="widget-card__status">{rosterErrorMessage}</div>
						{:else}
							<div class="roster-block">
								<h4>Игроки</h4>
								{#if !players.length}
									<div class="widget-card__status --small">Игроки не найдены</div>
								{:else if selectedViewMode === 'cards'}
									<div class="person-grid">
										{#each players as person}
											<div class="person-card" role="button" tabindex="0" on:click={() => openPlayer(person)} on:keypress={() => openPlayer(person)}>
												<div class="person-card__head">
													{#if person.photo}
														<img class="person-card__photo" src={person.photo} alt={getPersonTitle(person)}>
													{:else}
														<div class="person-card__placeholder">{getPersonInitials(person, 'P')}</div>
													{/if}
													<div>
														<strong>{getPersonTitle(person)}</strong>
														<p>{normalizeText(person.positionName)}</p>
													</div>
												</div>
												<div class="person-card__meta">
													<span>№ {normalizeText(person.displayNumber || person.num)}</span>
													<span>{person.height ? `${person.height} см` : '—'}</span>
													<span>{person.weight ? `${person.weight} кг` : '—'}</span>
													<span>{normalizeText(person.age)}</span>
												</div>
											</div>
										{/each}
									</div>
								{:else}
									<div class="stats_table-wrap">
										<table class="stats_table roster-table">
											<thead>
												<tr>
													<th>№</th>
													<th>Игрок</th>
													<th>Амплуа</th>
													<th>
														<button type="button" class="roster-table__sort" on:click={() => togglePlayersSort('height')}>
															{getSortLabel('height', 'Рост')}
														</button>
													</th>
													<th>
														<button type="button" class="roster-table__sort" on:click={() => togglePlayersSort('weight')}>
															{getSortLabel('weight', 'Вес')}
														</button>
													</th>
													<th>
														<button type="button" class="roster-table__sort" on:click={() => togglePlayersSort('age')}>
															{getSortLabel('age', 'Возраст')}
														</button>
													</th>
													<th>Гражданство</th>
												</tr>
											</thead>
											<tbody>
												{#each sortedPlayers as person}
													<tr class="roster-table__row-clickable" on:click={() => openPlayer(person)} on:keypress={() => openPlayer(person)} role="button" tabindex="0">
														<td>{normalizeText(person.displayNumber || person.num)}</td>
														<td>{getPersonTitle(person)}</td>
														<td>{normalizeText(person.positionName)}</td>
														<td>{person.height ? `${person.height} см` : '—'}</td>
														<td>{person.weight ? `${person.weight} кг` : '—'}</td>
														<td>{normalizeText(person.age)}</td>
														<td>{normalizeText(person.citizenship || person.nationality)}</td>
													</tr>
												{/each}
											</tbody>
											<tfoot>
												<tr>
													<th colspan="3">Среднее</th>
													<th style="text-align: center">{averageHeight === '—' ? '—' : `${averageHeight} см`}</th>
													<th style="text-align: center">{averageWeight === '—' ? '—' : `${averageWeight} кг`}</th>
													<th style="text-align: center">{averageAge}</th>
													<th>—</th>
												</tr>
											</tfoot>
										</table>
									</div>
								{/if}
							</div>

							<div class="roster-block">
								<h4>Тренеры</h4>
								{#if !coaches.length}
									<div class="widget-card__status --small">Тренеры не найдены</div>
								{:else if selectedViewMode === 'cards'}
									<div class="person-grid">
										{#each coaches as person}
											<div class="person-card" role="button" tabindex="0" on:click={() => openCoach(person)} on:keypress={() => openCoach(person)}>
												<div class="person-card__head">
													{#if person.photo}
														<img class="person-card__photo" src={person.photo} alt={getPersonTitle(person)}>
													{:else}
														<div class="person-card__placeholder">{getPersonInitials(person, 'C')}</div>
													{/if}
													<div>
														<strong>{getPersonTitle(person)}</strong>
														<p>{normalizeText(person.rank || person.shortRank || person.positionName)}</p>
													</div>
												</div>
												<div class="person-card__meta">
													<span>{normalizeText(person.age)}</span>
													<span>{normalizeText(person.citizenship || person.nationality)}</span>
												</div>
											</div>
										{/each}
									</div>
								{:else}
									<div class="stats_table-wrap">
										<table class="stats_table roster-table">
											<thead>
												<tr>
													<th>Тренер</th>
													<th>Роль</th>
													<th>Возраст</th>
													<th>Гражданство</th>
												</tr>
											</thead>
											<tbody>
												{#each coaches as person}
													<tr class="roster-table__row-clickable" on:click={() => openCoach(person)} on:keypress={() => openCoach(person)} role="button" tabindex="0">
														<td>{getPersonTitle(person)}</td>
														<td>{normalizeText(person.rank || person.shortRank || person.positionName)}</td>
														<td>{normalizeText(person.age)}</td>
														<td>{normalizeText(person.citizenship || person.nationality)}</td>
													</tr>
												{/each}
											</tbody>
										</table>
									</div>
								{/if}
							</div>
						{/if}
					</div>
				</TabPane>

				<TabPane title="Матчи">
					<div class="widget-tabs__body">
						<div class="roster-toolbar">
							<div class="roster-toolbar__item">
								<WidgetSelect
									className="--black"
									name="team-games-season"
									options={seasonOptions}
									bind:value={selectedGamesSeason}
									disabled={gamesLoading || !seasonOptions.length}
								/>
							</div>
						</div>

						{#if gamesLoading}
							<div class="widget-card__status"><Loader size="64px" /></div>
						{:else if gamesErrorMessage}
							<div class="widget-card__status">{gamesErrorMessage}</div>
						{:else if !games.length}
							<div class="widget-card__status --small">Матчи не найдены</div>
						{:else}
							<div class="games-list">
								{#each games as game}
									<GameCard {game} teamId={teamId} tag={getActiveTag()} compId={selectedCompId} />
								{/each}
							</div>
						{/if}
					</div>
				</TabPane>

				<TabPane title="Дополнительно">
					<div class="widget-tabs__body">
						<dl class="widget-stats">
							<div>
								<dt>Название</dt>
								<dd>{getTeamTitle(team)}</dd>
							</div>
							<div>
								<dt>Регион</dt>
								<dd>{getTeamSubtitle(team)}</dd>
							</div>
							<div><dt>Описание</dt><dd>{normalizeText(team.description)}</dd></div>
							<div><dt>Сайт</dt><dd>{normalizeText(team.webSite)}</dd></div>
							<div><dt>Турниров</dt><dd>{Array.isArray(team.comps) ? team.comps.length : 0}</dd></div>
							<div><dt>Сезоны</dt><dd>{Array.isArray(team.seasons) ? team.seasons.length : 0}</dd></div>
						</dl>
					</div>
				</TabPane>
			</TabView>
		</div>
	{/if}
</div>

<style>
	:host { display: block; }
	.widget-card {
		border: 1px solid #d7dce2;
		border-radius: 16px;
		padding: 18px;
		background: #fff;
		box-shadow: 0 6px 24px rgba(8, 22, 47, 0.05);
	}
	.widget-card__status {
		display: flex;
		justify-content: center;
		align-items: center;
		min-height: 160px;
		color: #53657b;
		text-align: center;
	}
	.widget-card__status.--small {
		min-height: 88px;
	}
	.profile__head {
		display: flex;
		gap: 16px;
		align-items: center;
	}
	.profile__media { flex: 0 0 auto; }
	.profile__image,
	.profile__placeholder {
		width: 88px;
		height: 88px;
		border-radius: 18px;
		object-fit: cover;
		background: #edf2f7;
	}
	.profile__placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 28px;
		font-weight: 700;
		color: #00244d;
	}
	.profile__title h3 {
		margin: 0;
		font-size: 22px;
		color: #122033;
	}
	.profile__title p {
		margin: 6px 0 0;
		color: #53657b;
	}
	.profile__metrics {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 12px;
		margin: 18px 0 8px;
	}
	.profile__metric {
		padding: 12px 14px;
		border-radius: 12px;
		background: #f7f9fc;
	}
	.profile__metric span {
		display: block;
		margin-bottom: 6px;
		font-size: 12px;
		text-transform: uppercase;
		color: #6b7a90;
	}
	.profile__metric strong {
		font-size: 16px;
		color: #122033;
	}
	.widget-tabs__body {
		padding: 4px 0 0;
	}
	.widget-stats {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 12px;
		margin: 0;
	}
	.widget-stats div {
		padding: 12px 14px;
		border-radius: 12px;
		background: #f7f9fc;
	}
	.widget-stats dt {
		margin-bottom: 6px;
		font-size: 12px;
		text-transform: uppercase;
		color: #6b7a90;
	}
	.widget-stats dd {
		margin: 0;
		color: #122033;
		font-weight: 600;
	}
	.roster-toolbar {
		display: flex;
		gap: 12px;
		align-items: center;
		margin-bottom: 16px;
		flex-wrap: wrap;
	}
	.roster-toolbar__item {
		min-width: 210px;
		flex: 0 1 260px;
	}
	.roster-toolbar__item--view {
		min-width: 170px;
		flex-basis: 190px;
	}
	.roster-toolbar__spacer {
		flex: 1 1 auto;
		min-width: 24px;
	}
	.roster-block {
		margin-bottom: 20px;
	}
	.roster-block h4 {
		margin: 0 0 12px;
		font-size: 18px;
		color: #122033;
	}
	.person-grid {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 12px;
	}
	.person-card {
		border: 1px solid #d7dce2;
		border-radius: 12px;
		padding: 12px;
		background: #fff;
		cursor: pointer;
		transition: box-shadow 0.2s ease, transform 0.2s ease;
	}
	.person-card:hover {
		box-shadow: 0 6px 18px rgba(0, 36, 77, 0.16);
		transform: translateY(-2px);
	}
	.person-card__head {
		display: flex;
		gap: 12px;
		align-items: center;
	}
	.person-card__head strong {
		display: block;
		font-size: 15px;
		color: #122033;
	}
	.person-card__head p {
		margin: 4px 0 0;
		font-size: 13px;
		color: #53657b;
	}
	.person-card__photo,
	.person-card__placeholder {
		width: 92px;
		height: 92px;
		border-radius: 14px;
		object-fit: cover;
		background: #edf2f7;
	}
	.person-card__placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 18px;
		font-weight: 700;
		color: #00244d;
	}
	.person-card__meta {
		display: flex;
		gap: 10px;
		flex-wrap: wrap;
		margin-top: 10px;
		font-size: 12px;
		color: #53657b;
	}
	.roster-table__row-clickable {
		cursor: pointer;
	}
	.roster-table__sort {
		border: none;
		background: transparent;
		font: inherit;
		font-weight: 700;
		color: inherit;
		cursor: pointer;
		padding: 0;
	}
	.roster-table tfoot th {
		background: #f7f9fc;
		font-weight: 700;
	}
	.games-list {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}
	.game-card {
		display: flex;
		align-items: center;
		gap: 0;
		padding: 12px;
		border: 1px solid #d7dce2;
		border-radius: 12px;
		background: #fff;
		cursor: pointer;
		transition: box-shadow 0.2s ease, transform 0.2s ease;
	}
	.game-card:hover {
		box-shadow: 0 6px 18px rgba(0, 36, 77, 0.16);
		transform: translateY(-2px);
	}
	.game-card__team {
		display: flex;
		align-items: center;
		gap: 8px;
		flex: 0 0 20%;
		min-width: 110px;
	}
	.game-card__team--left {
		justify-content: flex-start;
	}
	.game-card__team--right {
		flex-direction: row-reverse;
		justify-content: flex-end;
	}
	.game-card__center {
		flex: 1 1 60%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 6px;
		min-width: 200px;
		padding: 0 16px;
	}
	.game-card__comp {
		font-size: 12px;
		color: #6b7a90;
		text-transform: uppercase;
		font-weight: 600;
		line-height: 1;
	}
	.game-card__score-block {
		display: flex;
		align-items: center;
		gap: 6px;
		justify-content: center;
	}
	.game-card__logo,
	.game-card__logo-placeholder {
		width: 40px;
		height: 40px;
		border-radius: 8px;
		object-fit: cover;
		background: #edf2f7;
		flex: 0 0 40px;
	}
	.game-card__logo-placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 14px;
		font-weight: 700;
		color: #00244d;
	}
	.game-card__team-name {
		font-size: 14px;
		font-weight: 600;
		color: #122033;
		line-height: 1.2;
	}
	.game-card__score-value {
		font-size: 24px;
		font-weight: 700;
		color: #122033;
		width: 30px;
		text-align: center;
		line-height: 1;
	}
	.game-card__score-dash {
		color: #6b7a90;
		font-size: 20px;
		font-weight: 700;
	}
	.game-card__no-score {
		font-size: 20px;
		color: #6b7a90;
	}
	.game-card__datetime {
		display: flex;
		align-items: center;
		gap: 4px;
		font-size: 12px;
		color: #53657b;
		line-height: 1;
		justify-content: center;
		flex-wrap: wrap;
	}
	.game-card__date {
		font-weight: 600;
	}
	.game-card__time {
		font-weight: 600;
	}
	.game-card__separator {
		color: #d7dce2;
	}
	.game-card__arena {
		text-align: center;
	}
	@media (max-width: 1200px) {
		.person-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
	}
	@media (max-width: 900px) {
		.person-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
	}
	@media (max-width: 640px) {
		.profile__head { align-items: flex-start; }
		.profile__metrics,
		.widget-stats,
		.person-grid { grid-template-columns: 1fr; }
		.roster-toolbar {
			align-items: stretch;
		}
		.roster-toolbar__item,
		.roster-toolbar__item--view {
			min-width: 100%;
			flex-basis: 100%;
		}
		.roster-toolbar__spacer {
			display: none;
		}
		.game-card {
			flex-direction: column;
			align-items: stretch;
			gap: 8px;
			padding: 12px;
		}
		.game-card__team {
			flex: 0 0 auto;
			justify-content: space-between;
		}
		.game-card__team--left,
		.game-card__team--right {
			min-width: auto;
		}
		.game-card__center {
			flex: 1;
			min-width: auto;
			padding: 8px 0;
		}
	}
</style>
